import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageContainer, SurfaceCard, Table } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type { ContractListItem } from '../../domain/entities/sales.entity';
import {
  ContractStatusBadge,
  ContractTotalValue,
  formatDate,
  tenantLabel,
  ErrorState,
  getApiErrorMessage,
} from '../shared/display';
import { useContracts } from '../hooks/use-contracts';
import {
  EMPTY_CONTRACT_FILTERS,
  countActiveContractFilters,
  toContractQueryFilters,
  type ContractFilters,
} from '../shared/contracts-filters';
import { ContractsFiltersModal } from '../components/ContractsFiltersModal';
import { RefreshButton } from '@/shared/RefreshButton';
import { EntityLink } from '@/shared/components/entity-link';
import {
  readPageParam,
  writePageParam,
} from '@/shared/hooks/use-page-query-param';
import {
  readFiltersParams,
  writeFiltersParams,
} from '@/shared/hooks/use-url-filters';

const PER_PAGE = 10;

// A coluna "Contrato ID" saiu: o id já aparece ao abrir o item, e ocupava a
// primeira coluna com o dado menos legível da linha.
const columns: ColumnDef<ContractListItem>[] = [
  {
    key: 'product',
    header: 'Nome do Produto',
    render: (row) => (
      <span className="text-(--color-text-primary) text-xs">
        {row.productName ?? '---'}
      </span>
    ),
  },
  {
    key: 'contact',
    header: 'Contato Atrelado',
    // O contato é um usuário com tela própria — o nome leva até ela.
    render: (row) => (
      <span className="text-(--color-text-primary)">
        <EntityLink kind="user" id={row.userId} fallback="---">
          {row.contactName ?? '---'}
        </EntityLink>
      </span>
    ),
  },
  {
    key: 'businessUnit',
    header: 'BU',
    render: (row) => (
      <span className="text-(--color-text-secondary)">
        {tenantLabel(row.tenant)}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    width: '220px',
    render: (row) => <ContractStatusBadge status={row.status} />,
  },
  {
    key: 'totalValue',
    header: 'Valor total',
    width: '150px',
    render: (row) => <ContractTotalValue summary={row.valueSummary} />,
  },
  {
    key: 'startDate',
    header: 'Iniciado em',
    render: (row) => (
      <span className="text-(--color-text-secondary)">
        {formatDate(row.startDate)}
      </span>
    ),
  },
  {
    key: 'endDate',
    header: 'Encerrado em',
    render: (row) => (
      <span className="text-(--color-text-secondary)">
        {formatDate(row.endDate)}
      </span>
    ),
  },
];

export function ContractsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Página e filtros vivem na URL (não em useState) para sobreviver a
  // "Voltar" do detalhe do contrato — ver
  // primum-core-root/docs/plans/done/admin-dashboard-pagination-url-sync.md. Aplicar/limpar filtro
  // também reseta a página; as duas mudanças vão num único setSearchParams
  // (ver aviso em use-page-query-param.ts).
  const page = readPageParam(searchParams);
  const filters = readFiltersParams(searchParams, EMPTY_CONTRACT_FILTERS);
  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writePageParam(params, next);
        return params;
      },
      { replace: true },
    );
  };
  const setFiltersAndResetPage = (next: ContractFilters) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        writeFiltersParams(params, next, EMPTY_CONTRACT_FILTERS);
        writePageParam(params, 1);
        return params;
      },
      { replace: true },
    );
  };
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // Todos os filtros vão pro servidor — inclusive BU e produto, que o back
  // resolve pela BA atual, e o contato, que chega como `userId`.
  const queryFilters = useMemo(
    () => toContractQueryFilters(filters),
    [filters],
  );
  const { data, isLoading, isError, error, refetch, isFetching } = useContracts(
    page,
    PER_PAGE,
    queryFilters,
  );

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PER_PAGE)) : 1;
  const activeFilterCount = countActiveContractFilters(filters);
  const hasActiveFilter = activeFilterCount > 0;
  const contracts = data?.items ?? [];

  const handleApplyFilters = (next: ContractFilters) => {
    setFiltersAndResetPage(next);
  };

  const handleClearFilters = () => {
    setFiltersAndResetPage(EMPTY_CONTRACT_FILTERS);
  };

  return (
    <PageContainer maxWidth="6xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-(--color-text-primary) text-2xl font-semibold">
            Contratos
          </h1>
          <p className="text-(--color-text-secondary) mt-1 text-sm">
            Contratos da plataforma, dos mais recentes para os mais antigos.
            Tentativas não convertidas ficam ocultas; filtre por status para
            vê-las.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RefreshButton
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
          />
          <button
            type="button"
            onClick={() => setIsFiltersOpen(true)}
            className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) rounded-lg border px-5 py-2.5 text-sm font-medium transition"
          >
            Filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
        </div>
      </div>

      <ContractsFiltersModal
        open={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        filters={filters}
        onApply={handleApplyFilters}
      />

      <SurfaceCard className="space-y-5">
        {hasActiveFilter && (
          <div className="flex items-center justify-between gap-3 text-xs">
            <p className="text-(--color-text-secondary)">
              {activeFilterCount === 1
                ? '1 filtro aplicado'
                : `${activeFilterCount} filtros aplicados`}
              .
            </p>
            <button
              onClick={handleClearFilters}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary) underline"
            >
              Limpar filtros
            </button>
          </div>
        )}

        {isError ? (
          <ErrorState
            message={getApiErrorMessage(
              error,
              'Não foi possível carregar os contratos. Revise os filtros e tente novamente.',
            )}
          />
        ) : (
          <Table
            columns={columns}
            data={contracts}
            keyExtractor={(row) => row.id}
            loading={isLoading}
            emptyMessage={
              hasActiveFilter
                ? 'Nenhum contrato encontrado. Ajuste os filtros ou use Limpar filtros.'
                : 'Nenhum contrato encontrado.'
            }
            striped
            onRowClick={(row) => void navigate(`/contracts/${row.id}`)}
          />
        )}

        <div className="flex items-center justify-between">
          <p className="text-(--color-text-secondary) text-xs">
            {data ? (
              <>
                {data.total}{' '}
                {data.total === 1
                  ? 'contrato encontrado'
                  : 'contratos encontrados'}
              </>
            ) : null}
          </p>

          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || isLoading}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="text-(--color-text-secondary) text-xs">
                {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || isLoading}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      </SurfaceCard>
    </PageContainer>
  );
}
